import { z } from "zod";
import { canonicalResultValueSchema } from "./canonical-result.js";
export const sessionContractSchemaVersion = 1;
export const sessionContractCompactContextMaxChars = 5_200;
export const sessionContractExpandedContextMaxChars = 20_000;
export const artistAvailabilityCoverageKeys = {
    bookingSpan: "artist.availability.booking_span",
    calendarEvents: "artist.availability.calendar_events",
    travelEvents: "artist.availability.travel_events",
    accommodationEvents: "artist.availability.accommodation_events",
};
export const touringActualVsPipelineCoverageKeys = {
    actualRevenue: "finance.sun.touring_actual_revenue",
    pipelineRevenue: "finance.overture.touring_pipeline",
};
export const sessionContractCoverageProfileSlugs = [
    "artist.availability.full",
    "finance.touring.actual_vs_pipeline",
];
export const sessionContractCoverageProfileSlugSchema = z.enum(sessionContractCoverageProfileSlugs);
export const sessionContractCoverageProfileRefSchema = z.object({
    slug: sessionContractCoverageProfileSlugSchema,
    version: z.number().int().positive(),
}).strict();
export const coverageKeySchema = z.string().trim().min(3).max(160).regex(/^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+$/u, "coverage keys must be namespaced lowercase slugs");
const twinProductCoverageKeysSchema = z.array(coverageKeySchema).max(80);
const twinProductCoverageShape = {
    covers: twinProductCoverageKeysSchema.default([]),
    doesNotCover: twinProductCoverageKeysSchema.default([]),
};
function requireDisjointTwinProductCoverage(coverage, context) {
    const covered = new Set(coverage.covers);
    for (const [index, key] of coverage.doesNotCover.entries()) {
        if (covered.has(key)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["doesNotCover", index],
                message: `${key} cannot be both covered and excluded`,
            });
        }
    }
}
export const twinProductCoverageSchema = z.object(twinProductCoverageShape).strict()
    .superRefine(requireDisjointTwinProductCoverage);
/** Coverage required for a newly published or activated governed product. */
export const publicationReadyTwinProductCoverageSchema = z.object({
    covers: twinProductCoverageKeysSchema.min(1, "positive governed coverage is required"),
    doesNotCover: twinProductCoverageKeysSchema.min(1, "negative governed coverage is required"),
}).strict().superRefine(requireDisjointTwinProductCoverage);
export const sessionContractEvidenceRefSchema = z.object({
    runId: z.string().trim().min(1).max(200),
    toolCallId: z.string().trim().min(1).max(240).optional(),
    certificateId: z.string().trim().min(1).max(200).optional(),
    certificateSha256: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
    coverageItemId: z.string().trim().min(1).max(160).optional(),
    publicationVersionId: z.string().trim().min(1).max(200).optional(),
    compactFactEnvelopeSha256: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
    artifactId: z.string().trim().min(1).max(200).optional(),
    artifactVersionId: z.string().trim().min(1).max(200).optional(),
    artifactSha256: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
    observedAt: z.number().int().positive().optional(),
    freshnessPolicy: z.object({
        kind: z.literal("live_warehouse_query_snapshot"),
        reuse: z.literal("same_run_only"),
    }).strict().optional(),
    relation: z.string().trim().min(1).max(260).optional(),
    rowCount: z.number().int().nonnegative().optional(),
}).strict();
export const sessionRetrievalCertificateRefSchema = z.object({
    runId: z.string().trim().min(1).max(200),
    toolCallId: z.string().trim().min(1).max(240),
    certificateId: z.string().trim().min(1).max(200),
    certificateSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    publicationVersionId: z.string().trim().min(1).max(200),
    contractHash: z.string().regex(/^[0-9a-f]{64}$/u),
    relation: z.string().trim().min(1).max(260),
    querySha256: z.string().regex(/^[0-9a-f]{64}$/u),
    canonicalResultSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    compactFactEnvelopeSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    observedCutId: z.string().regex(/^ecut_[0-9a-f]{64}$/u).nullable(),
    observedAt: z.number().int().positive(),
    freshnessPolicy: z.string().trim().min(1).max(240),
    rowCount: z.number().int().nonnegative(),
    artifactRefs: z.array(z.object({
        artifactId: z.string().trim().min(1).max(200),
        kind: z.string().trim().min(1).max(80),
        contentSha256: z.string().regex(/^[0-9a-f]{64}$/u),
        rowCount: z.number().int().nonnegative(),
    }).strict()).max(8).default([]),
}).strict();
export const sessionGovernedContractRefSchema = z.object({
    publicationVersionId: z.string().trim().min(1).max(200),
    productSlug: z.string().trim().min(1).max(120),
    productVersion: z.number().int().positive(),
    contractHash: z.string().regex(/^[0-9a-f]{64}$/u),
    relation: z.string().trim().min(1).max(260),
    skillSlug: z.string().trim().min(1).max(120),
    covers: z.array(coverageKeySchema).max(80).default([]),
    doesNotCover: z.array(coverageKeySchema).max(80).default([]),
}).strict();
export const sessionContractAssumptionSchema = z.object({
    id: z.string().trim().min(1).max(160),
    statement: z.string().trim().min(1).max(320),
    source: z.enum(["router", "task_agent", "user", "runtime"]),
    status: z.enum(["open", "supported", "rejected", "inconclusive"]),
    evidenceRefs: z.array(sessionContractEvidenceRefSchema).max(64).default([]),
}).strict();
export const sessionContractCoverageStatusSchema = z.enum([
    "open",
    "in_progress",
    "resolved_present",
    "resolved_absent",
    "inconclusive",
    "failed",
]);
export const sessionContractCoverageItemSchema = z.object({
    id: z.string().trim().min(1).max(160),
    key: coverageKeySchema,
    label: z.string().trim().min(1).max(240),
    required: z.boolean().default(true),
    status: sessionContractCoverageStatusSchema.default("open"),
    gapReason: z.enum([
        "not_queried",
        "surface_not_selected",
        "selected_surface_does_not_cover",
        "query_failed",
        "conflicting_evidence",
        "coverage_undeclared",
        "coverage_ambiguous",
        "authority_changed",
        "freshness_expired",
    ]).optional(),
    boundContractRefs: z.array(z.string().trim().min(1).max(200)).max(32).default([]),
    evidenceRefs: z.array(sessionContractEvidenceRefSchema).max(64).default([]),
}).strict();
export const sessionContractObjectiveSchema = z.object({
    id: z.string().trim().min(1).max(160),
    summary: z.string().trim().min(1).max(500),
    status: z.enum(["active", "completed", "failed", "superseded"]),
    coverageProfiles: z.array(sessionContractCoverageProfileRefSchema).max(4).default([]),
    assumptions: z.array(sessionContractAssumptionSchema).max(16).default([]),
    coverage: z.array(sessionContractCoverageItemSchema).max(24).default([]),
    governedContracts: z.array(sessionGovernedContractRefSchema).max(16).default([]),
    openedAt: z.number().int().positive(),
    updatedAt: z.number().int().positive(),
}).strict().superRefine((objective, context) => {
    const assumptionIds = objective.assumptions.map((item) => item.id);
    if (new Set(assumptionIds).size !== assumptionIds.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["assumptions"], message: "assumption ids must be unique" });
    }
    const coverageIds = objective.coverage.map((item) => item.id);
    const coverageKeys = objective.coverage.map((item) => item.key);
    if (new Set(coverageIds).size !== coverageIds.length || new Set(coverageKeys).size !== coverageKeys.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["coverage"], message: "coverage ids and keys must be unique" });
    }
    const contractIds = objective.governedContracts.map((item) => item.publicationVersionId);
    const contractIdSet = new Set(contractIds);
    if (contractIdSet.size !== contractIds.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["governedContracts"], message: "governed contract refs must be unique" });
    }
    const profileSlugs = objective.coverageProfiles.map((item) => item.slug);
    if (new Set(profileSlugs).size !== profileSlugs.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["coverageProfiles"], message: "coverage profiles must be unique" });
    }
    for (const [index, item] of objective.coverage.entries()) {
        if (item.boundContractRefs.some((id) => !contractIdSet.has(id))) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["coverage", index, "boundContractRefs"], message: "bound refs must point to governed contracts" });
        }
        if (["resolved_present", "resolved_absent"].includes(item.status) && item.evidenceRefs.length === 0) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["coverage", index, "evidenceRefs"], message: "resolved coverage requires evidence" });
        }
    }
    if (JSON.stringify(compactSessionContractObjective(objective)).length >
        sessionContractCompactContextMaxChars) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: [],
            message: "active objective exceeds the compact runtime context budget",
        });
    }
});
export const sessionContractSchema = z.object({
    schemaVersion: z.literal(sessionContractSchemaVersion),
    revision: z.number().int().nonnegative(),
    activeObjectiveId: z.string().trim().min(1).max(160).nullable(),
    objectives: z.array(sessionContractObjectiveSchema).max(16),
}).strict().superRefine((contract, context) => {
    const ids = contract.objectives.map((objective) => objective.id);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["objectives"], message: "objective ids must be unique" });
    }
    if (contract.activeObjectiveId !== null) {
        const active = contract.objectives.filter((objective) => objective.id === contract.activeObjectiveId && objective.status === "active");
        if (active.length !== 1) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["activeObjectiveId"], message: "active objective must exist and be active" });
        }
    }
    if (contract.objectives.filter((objective) => objective.status === "active").length > 1) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["objectives"], message: "at most one objective may be active" });
    }
});
export const activeSessionContractProjectionSchema = z.object({
    schemaVersion: z.literal(sessionContractSchemaVersion),
    revision: z.number().int().nonnegative(),
    activeObjective: sessionContractObjectiveSchema.nullable(),
}).strict();
export function compactSessionContractObjective(objective) {
    const coverageKeys = new Set(objective.coverage.map((item) => item.key));
    return {
        objective: { id: objective.id, summary: objective.summary },
        ...(objective.coverageProfiles.length > 0
            ? { coverageProfiles: objective.coverageProfiles }
            : {}),
        assumptions: objective.assumptions.map(({ id, statement, status }) => ({ id, statement, status })),
        coverage: objective.coverage.map(({ id, key, status, gapReason, boundContractRefs }) => ({
            coverageItemId: id,
            coverageKey: key,
            status,
            gapReason,
            boundContractRefs,
        })),
        governedContracts: objective.governedContracts.map(({ publicationVersionId, productSlug, relation, skillSlug, covers, }) => ({
            publicationVersionId, productSlug, relation, skillSlug,
            covers: covers.filter((key) => coverageKeys.has(key)),
        })),
    };
}
export const sessionContractRouterPatchSchema = z.object({
    disposition: z.enum(["continue", "open_new"]),
    objectiveSummary: z.string().trim().min(1).max(500),
    coverageProfileSlugs: z.array(sessionContractCoverageProfileSlugSchema).max(4).default([]),
    requiredCoverage: z.array(z.object({
        key: coverageKeySchema,
        label: z.string().trim().min(1).max(240),
    }).strict()).min(1).max(24),
    assumptions: z.array(z.string().trim().min(1).max(320)).max(16).default([]),
    selectedProductSlugs: z.array(z.string().trim().min(1).max(120)).max(16).default([]),
}).strict();
export const sessionContractTaskPlanSchema = z.object({
    disposition: z.enum(["continue", "open_new"]),
    expectedContractRevision: z.number().int().nonnegative(),
    objectiveSummary: z.string().trim().min(1).max(500),
    requiredFacts: z.array(z.object({
        key: coverageKeySchema,
        label: z.string().trim().min(1).max(240),
        required: z.literal(true).default(true),
    }).strict()).max(24),
    assumptions: z.array(z.string().trim().min(1).max(320)).max(16).default([]),
    operationId: z.string().trim().min(1).max(240),
}).strict().superRefine((plan, context) => {
    if (plan.disposition === "open_new" && plan.requiredFacts.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["requiredFacts"],
            message: "open_new plans require at least one fact",
        });
    }
    const keys = plan.requiredFacts.map((fact) => fact.key);
    if (new Set(keys).size !== keys.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["requiredFacts"],
            message: "required fact coverage keys must be unique",
        });
    }
});
export const planSessionRetrievalInputSchema = sessionContractTaskPlanSchema.extend({
    runId: z.string().trim().min(1).max(200),
    workerId: z.string().trim().min(1).max(200),
    attemptNumber: z.number().int().positive(),
}).strict();
export const listSessionGovernedCoverageInputSchema = z.object({
    runId: z.string().trim().min(1).max(200),
    workerId: z.string().trim().min(1).max(200),
    attemptNumber: z.number().int().positive(),
}).strict();
const boundedTextList = (maxItems, maxChars) => z.array(z.string().trim().min(1).max(maxChars)).max(maxItems).default([]);
export const sessionGovernedCoverageProductCardSchema = z.object({
    productSlug: z.string().trim().min(1).max(120),
    productVersion: z.number().int().positive(),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(500),
    canonicalDefinition: z.string().trim().min(1).max(800),
    grain: z.string().trim().min(1).max(400),
    intents: boundedTextList(8, 160),
    unsupportedIntents: boundedTextList(8, 160),
    dimensions: boundedTextList(12, 80),
    measures: boundedTextList(12, 80),
    sourceRefs: boundedTextList(8, 160),
    limitations: boundedTextList(6, 240),
    matchingCoverageKeys: z.array(coverageKeySchema).max(8).default([]),
}).strict();
export const sessionGovernedCoverageCatalogItemSchema = z.object({
    key: coverageKeySchema,
    authority: z.enum(["exact_owner", "ambiguous", "explicitly_unsupported"]),
    ownerCount: z.number().int().nonnegative(),
    explicitlyExcluded: z.boolean(),
    products: z.array(sessionGovernedCoverageProductCardSchema).max(4).default([]),
}).strict();
export const listSessionGovernedCoverageOutputSchema = z.object({
    coverage: z.array(sessionGovernedCoverageCatalogItemSchema).max(200),
}).strict();
export const searchSessionGovernedContractsInputSchema = z.object({
    runId: z.string().trim().min(1),
    workerId: z.string().trim().min(1).max(200),
    attemptNumber: z.number().int().positive(),
    objectiveId: z.string().trim().min(1),
    coverageItemIds: z.array(z.string().trim().min(1)).min(1).max(16),
    limit: z.number().int().min(1).max(5).default(5),
}).strict();
export const expandSessionContractInputSchema = z.object({
    runId: z.string().trim().min(1),
    workerId: z.string().trim().min(1).max(200),
    attemptNumber: z.number().int().positive(),
    objectiveId: z.string().trim().min(1),
    expectedRevision: z.number().int().nonnegative(),
    publicationVersionId: z.string().trim().min(1),
    coverageItemIds: z.array(z.string().trim().min(1)).min(1).max(16),
    remainingContextChars: z.number().int().min(0).max(sessionContractExpandedContextMaxChars),
    operationId: z.string().trim().min(1).max(240),
}).strict();
export const mutateSessionContractInputSchema = z.object({
    runId: z.string().trim().min(1),
    workerId: z.string().trim().min(1).max(200),
    attemptNumber: z.number().int().positive(),
    objectiveId: z.string().trim().min(1),
    expectedRevision: z.number().int().nonnegative(),
    operation: z.discriminatedUnion("kind", [
        z.object({
            kind: z.literal("add_assumption"),
            statement: z.string().trim().min(1).max(320),
        }).strict(),
        z.object({
            kind: z.literal("add_gap"),
            key: coverageKeySchema,
            label: z.string().trim().min(1).max(240),
        }).strict(),
    ]),
    operationId: z.string().trim().min(1).max(240),
}).strict();
/**
 * A task-agent retrieval intent. Product and publication identifiers are
 * deliberately absent: the control plane derives them from the referenced
 * contract revision and rejects unbound coverage before creating a child run.
 */
export const sessionRetrievalBatchRequestSchema = z.object({
    parentRunId: z.string().trim().min(1).max(200),
    workerId: z.string().trim().min(1).max(200),
    attemptNumber: z.number().int().positive(),
    objectiveId: z.string().trim().min(1).max(160),
    expectedContractRevision: z.number().int().nonnegative(),
    operationId: z.string().trim().min(1).max(240),
    coverageItemIds: z.array(z.string().trim().min(1).max(160)).min(1).max(8),
    retrievalRequest: z.string().trim().min(1).max(2_000),
}).strict().superRefine((request, context) => {
    if (new Set(request.coverageItemIds).size !== request.coverageItemIds.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["coverageItemIds"],
            message: "coverage item ids must be unique",
        });
    }
});
export const readSessionRetrievalBatchInputSchema = z.object({
    parentRunId: z.string().trim().min(1).max(200),
    workerId: z.string().trim().min(1).max(200),
    attemptNumber: z.number().int().positive(),
    batchId: z.string().trim().min(1).max(200),
}).strict();
export const sessionRetrievalOutcomeStatusSchema = z.enum([
    "present",
    "absent",
    "inconclusive",
    "failure",
]);
const sessionRetrievalFactValueSchema = z.union([
    canonicalResultValueSchema,
    // Read compatibility for batches persisted by the protocol prototype.
    z.string().trim().min(1).max(500),
    z.number().finite(),
    z.boolean(),
]);
export const sessionRetrievalCertifiedFactSchema = z.object({
    label: z.string().trim().min(1).max(160),
    value: sessionRetrievalFactValueSchema,
    unit: z.string().trim().min(1).max(80).optional(),
    currency: z.string().regex(/^[A-Z]{3}$/u).optional(),
    grain: z.string().trim().min(1).max(240),
    timeBoundary: z.string().trim().min(1).max(240),
    sourceBoundary: z.string().trim().min(1).max(240),
    caveats: z.array(z.string().trim().min(1).max(240)).max(8).default([]),
    certificateIds: z.array(z.string().trim().min(1).max(200)).min(1).max(8),
}).strict();
export const sessionRetrievalSemanticBoundarySchema = z.object({
    publicationVersionId: z.string().trim().min(1).max(200),
    productSlug: z.string().trim().min(1).max(120),
    contractHash: z.string().regex(/^[0-9a-f]{64}$/u),
    canonicalDefinition: z.string().trim().min(1).max(1_200),
    grain: z.string().trim().min(1).max(400),
    dimensions: z.array(z.string().trim().min(1).max(160)).max(12),
    measures: z.array(z.string().trim().min(1).max(160)).max(12),
    sourceRefs: z.array(z.string().trim().min(1).max(240)).min(1).max(8),
    limitations: z.array(z.string().trim().min(1).max(320)).max(6),
    truncated: z.boolean(),
}).strict();
export const sessionRetrievalBatchOutcomeSchema = z.object({
    coverageItemId: z.string().trim().min(1).max(160),
    status: sessionRetrievalOutcomeStatusSchema,
    summary: z.string().trim().min(1).max(800),
    zeroRowAssertion: z.boolean(),
    facts: z.array(sessionRetrievalCertifiedFactSchema).max(12).default([]),
    evidenceRefs: z.array(sessionRetrievalCertificateRefSchema).max(8).default([]),
    semanticBoundary: sessionRetrievalSemanticBoundarySchema.optional(),
    failureCode: z.string().trim().min(1).max(120).optional(),
}).strict().superRefine((outcome, context) => {
    if (["present", "absent"].includes(outcome.status) && outcome.evidenceRefs.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["evidenceRefs"],
            message: "present and absent outcomes require certified evidence",
        });
    }
    if (outcome.status === "absent" && outcome.evidenceRefs.some((item) => item.rowCount !== 0)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["evidenceRefs"],
            message: "absent outcomes require zero-row evidence",
        });
    }
    if ((outcome.status === "absent") !== outcome.zeroRowAssertion) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["zeroRowAssertion"],
            message: "zero-row assertion must be true only for absent outcomes",
        });
    }
    if (outcome.status === "present" && !outcome.evidenceRefs.some((item) => item.rowCount > 0)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["evidenceRefs"],
            message: "present outcomes require positive-row evidence",
        });
    }
    if (outcome.status === "present" && outcome.facts.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["facts"],
            message: "present outcomes require at least one compact certified fact",
        });
    }
    if (outcome.status !== "present" && outcome.facts.length > 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["facts"],
            message: "only present outcomes may contain facts",
        });
    }
    if (outcome.status === "failure" && !outcome.failureCode) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["failureCode"],
            message: "failure outcomes require a failure code",
        });
    }
    if (outcome.status !== "failure" && outcome.failureCode) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["failureCode"],
            message: "only failure outcomes may contain a failure code",
        });
    }
    const evidenceIds = new Set(outcome.evidenceRefs.map((item) => item.certificateId));
    if (evidenceIds.size !== outcome.evidenceRefs.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["evidenceRefs"],
            message: "certificate ids must be unique within an outcome",
        });
    }
    for (const [index, fact] of outcome.facts.entries()) {
        if (new Set(fact.certificateIds).size !== fact.certificateIds.length) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["facts", index, "certificateIds"],
                message: "fact certificate ids must be unique",
            });
        }
        if (fact.certificateIds.some((id) => !evidenceIds.has(id))) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["facts", index, "certificateIds"],
                message: "fact certificate ids must reference evidence in this outcome",
            });
        }
    }
});
export const sessionRetrievalBatchResultSchema = z.object({
    batchId: z.string().trim().min(1).max(200),
    childRunId: z.string().trim().min(1).max(200),
    parentRunId: z.string().trim().min(1).max(200),
    sessionId: z.string().trim().min(1).max(200),
    objectiveId: z.string().trim().min(1).max(160),
    plannedContractRevision: z.number().int().nonnegative(),
    finalContractRevision: z.number().int().nonnegative(),
    requestedCoverageItemIds: z.array(z.string().trim().min(1).max(160)).min(1).max(8),
    status: z.enum(["completed", "failed", "cancelled"]),
    outcomes: z.array(sessionRetrievalBatchOutcomeSchema).max(8).default([]),
    batchFailureCode: z.string().trim().min(1).max(120).optional(),
    resultSha256: z.string().regex(/^[0-9a-f]{64}$/u),
}).strict().superRefine((result, context) => {
    const requested = result.requestedCoverageItemIds;
    const returned = result.outcomes.map((outcome) => outcome.coverageItemId);
    if (new Set(requested).size !== requested.length || new Set(returned).size !== returned.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["outcomes"],
            message: "requested and returned coverage item ids must be unique",
        });
    }
    if (returned.some((id) => !requested.includes(id))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["outcomes"],
            message: "outcomes may reference only requested coverage items",
        });
    }
    if (result.outcomes.some((outcome) => outcome.evidenceRefs.some((evidence) => evidence.runId !== result.childRunId))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["outcomes"],
            message: "certificate evidence must belong to the retrieval child run",
        });
    }
    if (result.status === "completed" && (returned.length !== requested.length || returned.some((id) => !requested.includes(id)))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["outcomes"],
            message: "completed batches require exactly one outcome for every requested gap",
        });
    }
    if (result.status === "completed" && result.batchFailureCode) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["batchFailureCode"],
            message: "completed batches cannot contain a batch failure code",
        });
    }
    if (result.status !== "completed" && !result.batchFailureCode) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["batchFailureCode"],
            message: "failed and cancelled batches require a batch failure code",
        });
    }
    if (result.finalContractRevision < result.plannedContractRevision) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["finalContractRevision"],
            message: "final contract revision cannot precede the planned revision",
        });
    }
});
export function activeSessionContractProjection(contract) {
    return activeSessionContractProjectionSchema.parse({
        schemaVersion: contract.schemaVersion,
        revision: contract.revision,
        activeObjective: contract.activeObjectiveId === null
            ? null
            : contract.objectives.find((objective) => objective.id === contract.activeObjectiveId) ?? null,
    });
}
export function unresolvedRequiredCoverage(objective) {
    return objective.coverage.filter((item) => item.required &&
        item.status !== "resolved_present" && item.status !== "resolved_absent");
}
